import LottieAnimation from "@/components/common/lottieAnimation/lottieAnimation.component";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import RichText from "@/components/common/richText/richText.component";
import React from "react";
import { cn } from "@/lib/utils";

export interface SignupSectionProps {
  badgeText?: string;
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonLink?: string;
  /** Optional second, outlined button next to the main one. */
  secondaryButton?: { label: string; link: string };
  animationKey?: ILottieAnimation["animation"];
  // Band colour. Its own prop (not part of className) because tailwind-merge
  // doesn't recognise custom colour names like bg-secondary-2 as conflicting
  // with bg-primary, so a className override would leave both classes on.
  background?: "bg-primary" | "bg-secondary-2";
  className?: string;
}

const SignupSection: React.FC<SignupSectionProps> = ({
  badgeText,
  title = "",
  subtitle = "We're here to help you every step of the way. Let's grow your business together.",
  buttonLabel = "",
  buttonLink = "#",
  secondaryButton,
  animationKey = "SIGNUP_BG",
  background = "bg-primary",
  className = "",
}) => {
  return (
    <div
      className={cn(
        background,
        "sm:min-h-[35rem] min-h-[30rem] py-16 text-white flex flex-col items-center justify-center relative overflow-hidden",
        className,
      )}
    >
      <div className="flex flex-col items-center justify-center gap-8 relative z-10">
        {badgeText && (
          // light-to-dark-gradient is bg-brand-gradient - the same orange
          // gradient as this section's own bg-primary background, so the
          // badge was nearly invisible here. glass-white (translucent white)
          // actually shows up against orange.
          <Badge
            variant="glass-white"
            className="font-medium text-white text-sm rounded-full whitespace-normal text-center leading-snug"
          >
            {badgeText}
          </Badge>
        )}
        <h2 className="max-w-[40rem] font-medium text-center px-6">{title}</h2>
        {subtitle && (
          <p className="max-w-[36rem] text-center px-6 sm:text-base/[1.7]">
            <RichText text={subtitle} />
          </p>
        )}
        {/* Both buttons share one size so they line up side by side
            (and stack at full width on mobile). */}
        <div className="flex w-full flex-col items-center justify-center gap-3 px-6 sm:w-auto sm:flex-row">
          <Link href={buttonLink} className="w-full sm:w-auto">
            <Button
              variant="gold-grad"
              className="h-12 w-full min-w-[11rem] px-6 py-0 text-base whitespace-nowrap hover:scale-105 transition-transform active:scale-100"
            >
              {buttonLabel}
            </Button>
          </Link>
          {secondaryButton && (
            <Link
              href={secondaryButton.link}
              className="inline-flex h-12 w-full min-w-[11rem] items-center justify-center rounded-[8px] border-2 border-white/80 px-6 text-base font-bold whitespace-nowrap text-white transition-colors hover:bg-white/10 sm:w-auto"
            >
              {secondaryButton.label}
            </Link>
          )}
        </div>
      </div>
      {animationKey && (
        <LottieAnimation
          animation={animationKey}
          loop
          className="absolute h-full"
        />
      )}
    </div>
  );
};

export default SignupSection;
